// TEMPORARY: Commented out for design review — restore when done.
// import IframeErrorPage from "./components/screens/home_screns/web_application/IframeErrorPage";
// function App() {
//   return (
//     <div className="w-screen h-screen flex">
//       <IframeErrorPage
//         errorType="blocked"
//         url="https://example.com"
//         onRetry={() => console.log("Retry clicked")}
//       />
//     </div>
//   );
// }
// export default App;

// App.tsx - Updated with comprehensive cross-account isolation
import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { useBrowserNavigationBlocker } from "./hooks/useBrowserNavigationBlocker";

import SignInCard from "./components/screens/Auth_flow/SignInCard";
import SignUpCard from "./components/screens/Auth_flow/SignUpCard";
import OnboardingScreen from "./components/screens/Auth_flow/Onboarding_Screen";
import Dashboard from "./components/screens/Dashboard";
import SharedFeedbackView from "./components/screens/home_screns/Shared_Feedback_view_page_by_developer";
import JiraCallbackHandler from "./components/screens/setting_tabs/jira/JiraCallbackHandler";
import SettingsPage from "./components/screens/dashboardscreens/SettingsPage";
import WorkspacePage from "./components/screens/dashboardscreens/WorkspacePage";
import FeedbackPage from "./components/screens/dashboardscreens/FeedbackPage";
import UploadPage from "./components/screens/dashboardscreens/UploadPage";

import {
  AuthRoute,
  ProtectedRoute,
  OnboardingRoute,
} from "./components/screens/Auth_flow/RouteProtection";
import { ProfileImageProvider } from "./contexts/ProfileImageContext";
import { WorkspaceIconProvider } from "./contexts/WorkspaceIconContext";

import { DeveloperTokenManager } from "./utils/developerTokenManager";
import { EnhancedTokenManager } from "./utils/tokenManager";
import { SubdomainManager } from "./utils/subdomainUtils";
import SharedFeedbackViewSkeleton from "./components/ui/SharedFeedbackViewSkeleton";
import { BASE_URL } from "./api/urls";
import { verifyDeveloperToken } from "./api/api-utils";
import { extractCardIdFromUrl } from "./utils/urlIdEncoder";

const clearSessionStorageSafely = () => {
  try {
    sessionStorage.clear();
  } catch (error) {
    console.warn('⚠️ Error clearing sessionStorage:', error);
  }
};

// Token refresh interval
let refreshInterval: NodeJS.Timeout | null = null;

const setupTokenRefresh = () => {
  if (refreshInterval) {
    clearInterval(refreshInterval);
  }

  refreshInterval = setInterval(async () => {
    const accessToken = EnhancedTokenManager.getAccessToken();
    if (accessToken) {
      console.log("🔄 Periodic token refresh check...");
      const isExpired = EnhancedTokenManager.isTokenExpired();

      if (isExpired) {
        console.log("⏰ Token expiring soon, refreshing...");
        const refreshed = await EnhancedTokenManager.refreshAccessToken();
        if (!refreshed) {
          console.warn("❌ Token refresh failed");
        } else {
          console.log("✅ Token refreshed successfully");
        }
      } else {
        console.log("✅ Token still valid");
      }
    }
  }, 4 * 60 * 60 * 1000);

  console.log("✅ Token refresh interval set up");
};

/**
 * Logout listener — clears all auth + workspace state and redirects to login.
 */
const setupLogoutListener = () => {
  const handleLogout = () => {
    console.log("🚫 Logout event detected - performing comprehensive cleanup");

    // ✅ Clear subdomain FIRST
    SubdomainManager.clearWorkspaceSubdomain();

    clearSessionStorageSafely();

    // ✅ Clear all auth tokens
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("current_user_id");

    // ✅ Clear ALL workspace-related data for ALL users
    try {
      const allKeys = Object.keys(localStorage);
      allKeys.forEach(key => {
        if (key.includes('workspace_id_user_') ||
            key.includes('user_profile_user_') ||
            key.includes('workspace_data_user_') ||
            key.includes('workspace_icon_user_') ||
            key.toLowerCase().includes('subdomain')) {
          localStorage.removeItem(key);
          console.log(`🧹 Cleared: ${key}`);
        }
      });
    } catch (error) {
      console.warn("Failed to clear workspace data:", error);
    }

    // ✅ Clear memory cache
    delete (window as any).__feebdack_tokens;
    delete (window as any).__current_user_email;

    // Clear all cached objects
    Object.keys(window).forEach(key => {
      if (key.includes('__feebdack_')) {
        delete (window as any)[key];
      }
    });

    const isOnSubdomain = SubdomainManager.isOnWorkspaceSubdomain();

    if (isOnSubdomain && !SubdomainManager.isLocalDevelopment()) {
      console.log("Redirecting to app domain from subdomain");
      const appDomain = `app.${SubdomainManager.getBaseDomain()}`;
      window.location.replace(`https://${appDomain}/?logout=true&_t=${Date.now()}`);
    } else {
      // On app domain, just reload to login
      window.location.replace('/?logout=true&_t=' + Date.now());
    }
  };

  window.addEventListener('user_logout', handleLogout);

  return () => {
    window.removeEventListener('user_logout', handleLogout);
  };
};

/**
 * Startup initialisation — token restoration and cleanup on logout redirect.
 */
if (typeof window !== "undefined") {
  const startTime = performance.now();
  console.log("🚀 App initialization - immediate token check");

  const urlParams = new URLSearchParams(window.location.search);
  const isLogout = urlParams.get("logout") === "true";

  if (isLogout) {
    console.log("🚫 Logout detected in URL - comprehensive cleanup");

    // ✅ STEP 1: Clear subdomain FIRST
    SubdomainManager.clearWorkspaceSubdomain();

    // STEP 2: Clear ALL storage
    clearSessionStorageSafely();

    // ✅ STEP 3: Clear auth tokens
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("current_user_id");

    // ✅ STEP 4: Clear ALL workspace data for ALL users
    try {
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (
          key.includes("workspace_id_user_") ||
          key.includes("user_profile_user_") ||
          key.includes("workspace_data_user_") ||
          key.includes("workspace_icon_user_") ||
          key.toLowerCase().includes("subdomain")
        ) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn("Failed to clear workspace data:", error);
    }

    // ✅ STEP 5: Clear memory
    delete (window as any).__feebdack_tokens;

    Object.keys(window).forEach(key => {
      if (key.includes('__feebdack_')) {
        delete (window as any)[key];
      }
    });

    // ✅ STEP 6: Clean URL immediately
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, document.title, cleanUrl);

    console.log("✅ Logout cleanup completed");
  } else {
    const hasExistingTokens = localStorage.getItem("access_token");

    // Token restoration from ?code= is handled in index.tsx bootstrap()
    // before React mounts — doing it here would steal the code from the URL
    // before bootstrap() runs, causing a race condition.
    if (hasExistingTokens) {
      console.log("✅ Tokens already in storage");

      // ✅ Verify existing tokens match subdomain
      setTimeout(() => {
        const userId = EnhancedTokenManager.getCurrentUserId();
        const storedSubdomain = SubdomainManager.getStoredWorkspaceSubdomain();

        if (userId && storedSubdomain) {
          const isValid = SubdomainManager.verifySubdomainOwnership(userId, storedSubdomain);
          if (!isValid) {
            console.error("⚠️ Subdomain ownership mismatch - clearing");
            SubdomainManager.clearWorkspaceSubdomain();
          }
        }
      }, 100);

      (async () => {
        const isExpired = EnhancedTokenManager.isTokenExpired();
        if (isExpired) {
          console.log("⏰ Token expired/expiring, refreshing on startup...");
          const refreshed = await EnhancedTokenManager.refreshAccessToken();
          if (refreshed) {
            console.log("✅ Startup token refresh successful");
          } else {
            console.warn("❌ Startup token refresh failed");
          }
        }
      })();

      // Token-transfer flags travel in the hash fragment — read from there
      const rawHash = window.location.hash.startsWith('#')
        ? window.location.hash.slice(1)
        : window.location.hash;
      const hashParams = new URLSearchParams(rawHash);

      const navBlocker = hashParams.get('nav_blocker');
      if (navBlocker === 'true') {
        sessionStorage.setItem('nav_blocker_active', 'true');
      }

      const showBetaPopup = hashParams.get('show_beta_popup');
      if (showBetaPopup === '1') {
        localStorage.setItem('showBetaPopupOnce', 'true');
      }

      if (navBlocker || showBetaPopup) {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname + window.location.search
        );
      }
    }
  }

  const endTime = performance.now();
  console.log(`✅ Token restoration completed in ${(endTime - startTime).toFixed(2)}ms`);
}

const RootHandler: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAuthAndRedirect = async () => {
      try {
        // ✅ Check for logout parameters first
        const urlParams = new URLSearchParams(location.search);
        const isLogout = urlParams.get('logout') === 'true';

        if (isLogout) {
          console.log('🚫 Logout parameter detected in root handler');
          SubdomainManager.clearWorkspaceSubdomain();
          clearSessionStorageSafely();
          navigate('/login', { replace: true });
          setIsChecking(false);
          return;
        }

        const logoutInProgress = sessionStorage.getItem('logout_in_progress');
        if (logoutInProgress === 'true') {
          console.log('🚫 Logout in progress in root handler');
          SubdomainManager.clearWorkspaceSubdomain();
          clearSessionStorageSafely();
          navigate('/login', { replace: true });
          setIsChecking(false);
          return;
        }

        const accessToken = EnhancedTokenManager.getAccessToken();
        const refreshToken = EnhancedTokenManager.getRefreshToken();

        if (!accessToken || !refreshToken) {
          console.log('❌ No tokens found, redirecting to /login');
          SubdomainManager.clearWorkspaceSubdomain();
          navigate('/login', { replace: true });
          setIsChecking(false);
          return;
        }

        console.log('🔍 Validating token...');
        const tokenValid = await EnhancedTokenManager.ensureValidToken();
        if (!tokenValid) {
          console.log('❌ Token refresh failed, redirecting to /login');
          SubdomainManager.clearWorkspaceSubdomain();
          navigate('/login', { replace: true });
          setIsChecking(false);
          return;
        }

        const userProfile = EnhancedTokenManager.getUserProfile();

        if (userProfile) {
          if (userProfile.onboarded) {
            console.log('✅ User is authenticated and onboarded, redirecting to dashboard');
            navigate('/dashboard', { replace: true });
          } else {
            console.log('⚠️ User is authenticated but not onboarded, redirecting to onboarding');
            navigate('/onboarding', { replace: true });
          }
          setIsChecking(false);
          return;
        }

        const payload = EnhancedTokenManager.decodeJWT(accessToken);
        if (payload && payload.workspace_id && payload.workspace_id > 0) {
          console.log('✅ JWT indicates onboarded user, redirecting to dashboard');
          navigate('/dashboard', { replace: true });
        } else {
          console.log('⚠️ No onboarding info found, redirecting to onboarding');
          navigate('/onboarding', { replace: true });
        }

        setIsChecking(false);
      } catch (error) {
        console.error('❌ Error in root handler:', error);
        SubdomainManager.clearWorkspaceSubdomain();
        navigate('/login', { replace: true });
        setIsChecking(false);
      }
    };

    checkAuthAndRedirect();
  }, [navigate, location]);

  if (isChecking) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="fixed top-0 left-0 right-0 h-1 bg-gray-200 overflow-hidden z-50">
          <div className="h-full bg-blue-600 animate-progress-bar"></div>
        </div>

        <style>{`
          @keyframes progressBar {
            0% {
              width: 0%;
              margin-left: 0%;
            }
            50% {
              width: 75%;
              margin-left: 0%;
            }
            100% {
              width: 0%;
              margin-left: 100%;
            }
          }

          .animate-progress-bar {
            animation: progressBar 1.5s ease-in-out infinite;
          }
        `}</style>
      </div>
    );
  }

  return null;
};

const OptimizedDeepLinkHandler: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleDeepLink = async () => {
      try {
        const urlParams = new URLSearchParams(location.search);
        const code = urlParams.get("code");
        const email = urlParams.get("developer");

        const pathParts = location.pathname.split("/");
        const cardIdParam = pathParts[pathParts.length - 1];

        if (!code || !email || !cardIdParam) {
          setError("Invalid or incomplete feedback link");
          setIsLoading(false);
          return;
        }

        const cardId = extractCardIdFromUrl(cardIdParam);
        if (!cardId) {
          setError("Invalid card ID in link");
          setIsLoading(false);
          return;
        }

        const decodedEmail = decodeURIComponent(email);

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(decodedEmail)) {
          setError("Invalid email in link");
          setIsLoading(false);
          return;
        }

        // Exchange the share code for an access token
        const tokenResult = await verifyDeveloperToken(code);
        if (!tokenResult.success || !tokenResult.data) {
          setError("Failed to verify feedback link. It may have expired.");
          setIsLoading(false);
          return;
        }

        const accessToken = tokenResult.data.access_token;

        DeveloperTokenManager.storeToken(accessToken, decodedEmail, cardId);
        localStorage.setItem("last_developer_email", decodedEmail);

        const existingToken = EnhancedTokenManager.getAccessToken();

        if (!existingToken) {
          navigate("/", {
            state: {
              returnUrl: `/view-card/${cardId}?fromDeepLink=true`,
              message: "Please sign in to view this feedback",
              developerEmail: decodedEmail,
              _cachedToken: accessToken,
              _cachedCardId: cardId,
            },
            replace: true,
          });
          return;
        }

        navigate("/shared-feedback-view", {
          state: {
            cardId: cardId,
            fromDeepLink: true,
            returnToDashboard: true,
            developerEmail: decodedEmail,
            _cachedToken: accessToken,
            activeTab: "all",
          },
          replace: true,
        });
      } catch (error) {
        console.error("❌ Deep link processing error:", error);
        setError("Failed to process feedback link");
        setIsLoading(false);
      }
    };

    if (location.pathname.startsWith("/view-card/")) {
      handleDeepLink();
    }
  }, [location, navigate]);

  if (isLoading) {
    return <SharedFeedbackViewSkeleton />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-6 max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold mb-2 text-gray-900">Link Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate("/dashboard")}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return null;
};

const OptimizedSharedFeedbackViewWrapper: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [feedbackData, setFeedbackData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const state = location.state as {
    cardId: number;
    fromDeepLink?: boolean;
    returnToDashboard?: boolean;
    developerEmail?: string;
    _cachedToken?: string;
    activeTab?: string;
  };

  useEffect(() => {
    const fetchCardData = async () => {
      if (state?.cardId) {
        try {
          const storedViewedCards = localStorage.getItem("viewed_developer_cards");
          const viewedSet = storedViewedCards ? new Set(JSON.parse(storedViewedCards)) : new Set();
          viewedSet.add(state.cardId);
          localStorage.setItem("viewed_developer_cards", JSON.stringify(Array.from(viewedSet)));
        } catch (e) {
          console.warn("⚠️ Failed to mark card as viewed:", e);
        }
      }

      try {
        const developerEmail = state.developerEmail || localStorage.getItem("last_developer_email");

        if (!developerEmail) {
          setError("Developer credentials not found");
          setIsLoading(false);
          return;
        }

        let token: string | null = state._cachedToken || null;

        if (!token) {
          token = await DeveloperTokenManager.getDeveloperTokenForEmail(developerEmail);
        }

        if (!token) {
          setError("Authentication token not found");
          setIsLoading(false);
          return;
        }

        const response = await fetch(`${BASE_URL}/feedback/developer/feedbacks`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Developer-Email": developerEmail,
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            DeveloperTokenManager.removeToken(developerEmail, token);
            localStorage.removeItem("last_developer_email");
            throw new Error("Authentication expired. Please use the link from your email again.");
          }
          throw new Error(`Failed to fetch feedback: ${response.status}`);
        }

        const allCards = await response.json();
        const card = allCards.find((c: any) => c.card_id === state.cardId);

        if (!card) {
          throw new Error("Card not found");
        }

        setFeedbackData(card);
        setIsLoading(false);
      } catch (error) {
        console.error("❌ Failed to load card:", error);
        setError(error instanceof Error ? error.message : "Failed to load feedback");
        setIsLoading(false);
      }
    };

    fetchCardData();
  }, [state]);

  if (isLoading) {
    return <SharedFeedbackViewSkeleton />;
  }

  if (error || !feedbackData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-6 max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold mb-2 text-gray-900">Error Loading Feedback</h2>
          <p className="text-gray-600 mb-6">{error || "Feedback not found"}</p>
          <button
            onClick={() => navigate("/dashboard", { state: { activeTab: "all" } })}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const getFullImageUrl = (imagePath: string | null | undefined): string | undefined => {
    if (!imagePath) return undefined;
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) return imagePath;
    if (imagePath.startsWith("data:image/")) return imagePath;
    return `${BASE_URL}${imagePath.startsWith("/") ? "" : "/"}${imagePath}`;
  };

  const feedbackItems = (feedbackData.feedbacks ?? [])
    .filter((f: any) => f.message || f.screenshot_url || f.recording_url || f.voice_recording_url)
    .map((f: any, index: number) => ({
      id: f.id.toString(),
      type: f.screenshot_url ? "screenshot" : "recording",
      timestamp: new Date(f.created_at).getTime(),
      annotatedImage: getFullImageUrl(f.screenshot_url),
      originalImage: getFullImageUrl(f.screenshot_url),
      dataUrl: getFullImageUrl(f.recording_url),
      feedback: f.message,
      hasAudio: !!f.voice_recording_url,
      audioUrl: getFullImageUrl(f.voice_recording_url),
      uniqueFeedbackId: f.id.toString(),
      apiFeedbackId: f.id,
      order: index + 1,
      user: { name: "Developer", avatar: "D" },
      recordingBlob: null,
      technicalContext: f.technical_context || undefined,
    }));

  return (
    <SharedFeedbackView
      feedbackName={feedbackData.card_name}
      totalFeedbacks={feedbackItems.length}
      feedbackItems={feedbackItems}
      cardId={state?.cardId}
      isSubmittedMode={true}
      onBack={() => navigate("/dashboard", { state: { activeTab: "all" }, replace: true })}
    />
  );
};

const MobileBlocker: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  if (isMobile) {
    return (
      <div
        style={{
          minHeight: "100dvh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: "#ffffff",
          padding: "24px",
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: "20px",
            background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 28,
            boxShadow: "0 8px 32px rgba(99,102,241,0.2)",
          }}
        >
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="5" y="2" width="14" height="20" rx="2" />
            <line x1="12" y1="18" x2="12.01" y2="18" />
          </svg>
        </div>

        <h1 style={{ color: "#111827", fontSize: 22, fontWeight: 700, margin: "0 0 12px", fontFamily: "system-ui, sans-serif" }}>
          Mobile Not Supported
        </h1>
        <p style={{ color: "#6b7280", fontSize: 15, lineHeight: 1.6, maxWidth: 300, margin: "0 0 32px", fontFamily: "system-ui, sans-serif" }}>
          Cluva is designed for tablets and desktops. Please open it on a device with a larger screen.
        </p>


      </div>
    );
  }

  return <>{children}</>;
};

function App() {
  useBrowserNavigationBlocker();

  useEffect(() => {
    setupTokenRefresh();
    const cleanupLogoutListener = setupLogoutListener();

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
      cleanupLogoutListener();
    };
  }, []);

  return (
    <MobileBlocker>
    <WorkspaceIconProvider>
      <ProfileImageProvider>
        <Router>
          <Routes>
            {/* Root Route */}
            <Route path="/" element={<RootHandler />} />

            {/* Login Route */}
            <Route
              path="/login"
              element={
                <AuthRoute>
                  <SignInCard />
                </AuthRoute>
              }
            />

            {/* Sign Up Route */}
            <Route
              path="/signup"
              element={
                <AuthRoute>
                  <SignUpCard />
                </AuthRoute>
              }
            />

            {/* Onboarding Route */}
            <Route
              path="/onboarding"
              element={
                <OnboardingRoute>
                  <OnboardingScreen />
                </OnboardingRoute>
              }
            />

            {/* Protected Dashboard Route — nested layout */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            >
              <Route path="workspace/:cardId" element={<WorkspacePage />} />
              <Route path="feedback/:cardId" element={<FeedbackPage />} />
              <Route path="upload/:cardId" element={<UploadPage />} />
              <Route path="settings/*" element={<SettingsPage />} />
            </Route>

            {/* Jira OAuth callback */}
            <Route path="/jira/callback" element={<JiraCallbackHandler />} />

            {/* Deep link routes */}
            <Route path="/view-card/:cardId" element={<OptimizedDeepLinkHandler />} />
            <Route path="/shared-feedback-view" element={<OptimizedSharedFeedbackViewWrapper />} />
          </Routes>
        </Router>
      </ProfileImageProvider>
    </WorkspaceIconProvider>
    </MobileBlocker>
  );
}

export default App;
